import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { api } from '../api/client.js';
import { pmApi } from '../api/pm.js';
import { getRecentItems } from '../lib/recentItems.js';

/**
 * Cmd+K / Ctrl+K command palette.
 *
 * Always mounted (hidden when closed). Opens via global keyboard listener,
 * portals to document.body, traps focus on the input, and supports arrow-
 * key navigation + Enter to select + Escape/backdrop to close.
 *
 * Search sources, queried in parallel and grouped in the UI:
 *   - Issues   GET /search?q=…&limit=5  (graceful: tries /search; if absent
 *              falls back to GET /issues with project list filter)
 *   - Projects from cached project list
 *   - Users    GET /users/search?q=…&limit=3
 *   - Quick actions (static)
 */

const QUICK_ACTIONS = [
  { id: 'create-issue',  label: 'Create issue',         hint: 'Open the create issue form',     run: (nav) => nav('/pm/projects') },
  { id: 'goto-board',    label: 'Go to board',          hint: 'Open the active project board',  run: (nav, ctx) => nav(ctx.lastProject ? `/pm/projects/${ctx.lastProject}/board` : '/pm/projects') },
  { id: 'goto-backlog',  label: 'Go to backlog',        hint: 'Open the active project backlog',run: (nav, ctx) => nav(ctx.lastProject ? `/pm/projects/${ctx.lastProject}/backlog` : '/pm/projects') },
  { id: 'my-issues',     label: 'My open issues',       hint: 'Issues assigned to me',          run: (nav) => nav('/search?assignee=me&status=open') },
  { id: 'my-approvals',  label: 'My approvals inbox',   hint: 'Pending workflow approvals',     run: (nav) => nav('/workflows/my-approvals') },
];

function highlight(text, query) {
  if (!query) return text;
  const idx = String(text).toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text;
  const before = text.slice(0, idx);
  const match = text.slice(idx, idx + query.length);
  const after = text.slice(idx + query.length);
  return (<>{before}<strong>{match}</strong>{after}</>);
}

export default function CommandPalette() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ issues: [], projects: [], users: [] });
  const [recent, setRecent] = useState([]);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(false);

  // Module-level last-project cache (used by quick actions)
  const lastProject = (typeof localStorage !== 'undefined' && localStorage.getItem('bos.lastProject')) || null;

  // ── Global open/close keybinding ──────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      const isOpenCombo = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (isOpenCombo) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && open) {
        e.preventDefault();
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // ── Auto-focus + reset on open ─────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    setRecent(getRecentItems());
    // Microtask delay so the input is mounted before we focus.
    queueMicrotask(() => inputRef.current?.focus());
  }, [open]);

  // ── Debounced search ───────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (!query) {
      setResults({ issues: [], projects: [], users: [] });
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      const [issuesRes, usersRes, projectsRes] = await Promise.all([
        searchIssues(query).catch(() => []),
        searchUsers(query).catch(() => []),
        searchProjects(query).catch(() => []),
      ]);
      setResults({ issues: issuesRes, projects: projectsRes, users: usersRes });
      setLoading(false);
    }, 150);
    return () => clearTimeout(handle);
  }, [query, open]);

  // ── Build a flat list for keyboard navigation ──────────────────────
  const flatItems = useMemo(() => {
    const items = [];
    if (!query) {
      QUICK_ACTIONS.forEach((qa) => items.push({ kind: 'action', id: qa.id, label: qa.label, hint: qa.hint, run: () => qa.run(navigate, { lastProject }) }));
      recent.forEach((r) => items.push({ kind: 'recent', id: 'recent-' + r.key, label: r.title, sub: r.key, run: () => navigate(r.url) }));
    } else {
      results.issues.forEach((i) => items.push({
        kind: 'issue',
        id: 'issue-' + i.id,
        label: i.title,
        sub: `${i.project_key || i.key?.split('-')[0] || ''}-${i.issue_no || ''}`,
        badge: i.key || `${i.project_key || ''}-${i.issue_no || ''}`,
        run: () => {
          const k = i.key || `${i.project_key}-${i.issue_no}`;
          navigate(`/pm/issues/${k}`);
        },
      }));
      results.projects.forEach((p) => items.push({
        kind: 'project',
        id: 'project-' + p.id,
        label: `${p.key} — ${p.name}`,
        sub: 'Project',
        run: () => {
          if (typeof localStorage !== 'undefined') localStorage.setItem('bos.lastProject', p.key);
          navigate(`/pm/projects/${p.key}/board`);
        },
      }));
      results.users.forEach((u) => items.push({
        kind: 'user',
        id: 'user-' + u.id,
        label: u.name || u.email,
        sub: u.email,
        run: () => navigate(`/admin/users/${u.id}/supervisor-config`),
      }));
    }
    return items;
  }, [query, recent, results, navigate, lastProject]);

  // Clamp active index to bounds
  useEffect(() => { if (active >= flatItems.length) setActive(Math.max(0, flatItems.length - 1)); }, [flatItems.length, active]);

  // ── Modal keyboard handler ─────────────────────────────────────────
  function onModalKey(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = flatItems[active];
      if (item) {
        item.run();
        setOpen(false);
      }
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Command palette"
      className="cmd-palette-backdrop"
      data-testid="cmd-palette"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.45)',
        zIndex: 10_000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '12vh',
      }}
      onClick={() => setOpen(false)}
    >
      <div
        className="cmd-palette"
        style={{
          background: '#fff',
          width: 'min(90vw, 560px)',
          maxHeight: '70vh',
          borderRadius: 8,
          boxShadow: '0 18px 50px rgba(0,0,0,0.18)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onModalKey}
      >
        <input
          ref={inputRef}
          type="text"
          placeholder="Search issues, projects, people, or run a command…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Command palette search"
          autoComplete="off"
          style={{
            border: 'none',
            outline: 'none',
            padding: '14px 16px',
            fontSize: 15,
            borderBottom: '1px solid #e2e8f0',
          }}
        />

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {loading && <div style={{ padding: 12, color: '#94a3b8', fontSize: 12 }}>Searching…</div>}

          {!query && (
            <Group label="Quick Actions">
              {QUICK_ACTIONS.map((qa, i) => (
                <ResultRow
                  key={qa.id}
                  data-testid={`cmd-row-${qa.id}`}
                  active={active === i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => { qa.run(navigate, { lastProject }); setOpen(false); }}
                  label={qa.label}
                  sub={qa.hint}
                  kind="action"
                />
              ))}
            </Group>
          )}

          {!query && recent.length > 0 && (
            <Group label="Recent">
              {recent.map((r, i) => {
                const idx = QUICK_ACTIONS.length + i;
                return (
                  <ResultRow
                    key={r.key}
                    data-testid={`cmd-recent-${r.key}`}
                    active={active === idx}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => { navigate(r.url); setOpen(false); }}
                    label={r.title}
                    sub={r.key}
                    kind="recent"
                  />
                );
              })}
            </Group>
          )}

          {query && results.issues.length > 0 && (
            <Group label="Issues">
              {results.issues.map((i, ix) => {
                const flatIdx = ix;
                return (
                  <ResultRow
                    key={i.id}
                    active={active === flatIdx}
                    onMouseEnter={() => setActive(flatIdx)}
                    onClick={() => {
                      const k = i.key || `${i.project_key}-${i.issue_no}`;
                      navigate(`/pm/issues/${k}`);
                      setOpen(false);
                    }}
                    badge={i.key || `${i.project_key}-${i.issue_no}`}
                    label={highlight(i.title, query)}
                    sub={i.project_name || ''}
                    kind="issue"
                  />
                );
              })}
            </Group>
          )}

          {query && results.projects.length > 0 && (
            <Group label="Projects">
              {results.projects.map((p, ix) => {
                const flatIdx = results.issues.length + ix;
                return (
                  <ResultRow
                    key={p.id}
                    active={active === flatIdx}
                    onMouseEnter={() => setActive(flatIdx)}
                    onClick={() => {
                      if (typeof localStorage !== 'undefined') localStorage.setItem('bos.lastProject', p.key);
                      navigate(`/pm/projects/${p.key}/board`);
                      setOpen(false);
                    }}
                    badge={p.key}
                    label={highlight(p.name, query)}
                    sub="Project"
                    kind="project"
                  />
                );
              })}
            </Group>
          )}

          {query && results.users.length > 0 && (
            <Group label="People">
              {results.users.map((u, ix) => {
                const flatIdx = results.issues.length + results.projects.length + ix;
                return (
                  <ResultRow
                    key={u.id}
                    active={active === flatIdx}
                    onMouseEnter={() => setActive(flatIdx)}
                    onClick={() => { navigate(`/admin/users/${u.id}/supervisor-config`); setOpen(false); }}
                    label={highlight(u.name || u.email, query)}
                    sub={u.email}
                    kind="user"
                  />
                );
              })}
            </Group>
          )}

          {query && !loading && flatItems.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: '#94a3b8' }}>
              No results for "{query}".{' '}
              <a href={`/search?q=${encodeURIComponent(query)}`} onClick={(e) => { e.preventDefault(); navigate(`/search?q=${encodeURIComponent(query)}`); setOpen(false); }}>
                Search all issues →
              </a>
            </div>
          )}
        </div>

        <div style={{ borderTop: '1px solid #e2e8f0', padding: '8px 12px', fontSize: 11, color: '#64748b', display: 'flex', justifyContent: 'space-between' }}>
          <span><kbd>Cmd</kbd>+<kbd>K</kbd></span>
          <span><kbd>↑</kbd>/<kbd>↓</kbd> navigate · <kbd>Enter</kbd> select · <kbd>Esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Group({ label, children }) {
  return (
    <div>
      <div style={{ padding: '8px 16px 4px', fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
        {label}
      </div>
      {children}
    </div>
  );
}

function ResultRow({ active, label, sub, badge, onClick, onMouseEnter, kind, ...rest }) {
  return (
    <div
      role="option"
      aria-selected={active}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      data-kind={kind}
      style={{
        padding: '8px 16px',
        background: active ? '#f1f5f9' : 'transparent',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 13,
      }}
      {...rest}
    >
      {badge && <code style={{ fontSize: 11, padding: '2px 6px', background: '#e2e8f0', borderRadius: 3, fontFamily: 'monospace' }}>{badge}</code>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: '#64748b' }}>{sub}</div>}
      </div>
    </div>
  );
}

// ── Search adapters (graceful fallback when /search isn't deployed yet) ─
async function searchIssues(q) {
  try {
    const res = await api.get('/search', { params: { q, limit: 5 } });
    const data = res.data?.data;
    return data?.items || data?.data || data || [];
  } catch {
    // Fallback: scan recent issues from /issues and filter client-side.
    const res = await pmApi.issues({ q, limit: 5 });
    return res?.items || [];
  }
}

async function searchUsers(q) {
  try {
    const res = await api.get('/users/search', { params: { q, limit: 3 } });
    return res.data?.data?.items || res.data?.data || [];
  } catch {
    return [];
  }
}

async function searchProjects(q) {
  try {
    const res = await pmApi.projects();
    const all = res?.items || res?.projects || res || [];
    const lc = q.toLowerCase();
    return all.filter((p) => (p.key || '').toLowerCase().includes(lc) || (p.name || '').toLowerCase().includes(lc)).slice(0, 5);
  } catch {
    return [];
  }
}
